import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useClientAccess } from '../../hooks/useClientAccess';
import { useI18n } from '../../contexts/I18nContext';
import { apiClient } from '../../services/apiClient';
import { Pagination } from '../common/Pagination';
import { FirmwareVersion, DeviceUpdateStatus, OtaUpdate } from '../../types/otaUpdate';
import { FirmwareDetailModal } from './FirmwareDetailModal';
import { CopyUrlButton } from './CopyUrlButton';

interface OtaUpdatesOverviewProps {
  refreshTrigger?: number;
}

interface FirmwareApiItem {
  id: number;
  model: string;
  type: string;
  version: string;
  file_url: string;
  from_version: string | null;
  file_size: number | null;
  checksum: string | null;
  changelog: string | null;
  is_active: boolean;
  client_id: string | null;
  created_at: string;
  updated_at: string;
}

interface FirmwareApiResponse {
  items: FirmwareApiItem[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

interface OtaStatsResponse {
  total_firmware_versions: number;
  total_tracked_devices: number;
  device_models: string[];
}

interface DeviceVersionApiItem {
  id: number;
  device_id: string;
  model: string;
  serial: string | null;
  type: string;
  version: string;
  last_check: string;
  last_update: string | null;
  created_at: string;
  updated_at: string;
}

interface DeviceVersionsResponse {
  items: DeviceVersionApiItem[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

export const OtaUpdatesOverview: React.FC<OtaUpdatesOverviewProps> = ({ refreshTrigger }) => {
  const { isAuthenticated } = useAuth();
  const { isAdmin, isSuperAdmin } = useClientAccess();
  const canSeeInactive = isAdmin || isSuperAdmin;
  const { formatDate, formatDateTime, t } = useI18n();
  // Soft-delete: admins can opt in to see deactivated firmware. Default off.
  const [showInactive, setShowInactive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [firmwareVersions, setFirmwareVersions] = useState<FirmwareVersion[]>([]);
  const [firmwareItems, setFirmwareItems] = useState<FirmwareApiItem[]>([]);
  const [deviceStatus, setDeviceStatus] = useState<DeviceUpdateStatus[]>([]);
  const [pendingUpdates, setPendingUpdates] = useState<OtaUpdate[]>([]);
  const [otaStats, setOtaStats] = useState<OtaStatsResponse | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'firmware' | 'pending'>('firmware');
  const [selectedFirmware, setSelectedFirmware] = useState<FirmwareApiItem | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Firmware tab filter state. Search is debounced + pushed to the
  // server (`?search=`); model / type / active are server-side too.
  // Page + pageSize drive offset pagination.
  const [fwSearch, setFwSearch] = useState<string>('');
  const [fwModel, setFwModel] = useState<string>('');
  const [fwType, setFwType] = useState<string>('');
  const [fwActive, setFwActive] = useState<'all' | 'active' | 'inactive'>('all');
  const [fwPage, setFwPage] = useState(1);
  const [fwPageSize, setFwPageSize] = useState(25);
  const [fwTotal, setFwTotal] = useState(0);
  const [fwTotalPages, setFwTotalPages] = useState(0);

  // Reset to page 1 when a server-side filter flips. `fwSearch` and
  // `fwActive` are client-only over the current page so they don't
  // require a refetch.
  useEffect(() => {
    setFwPage(1);
  }, [fwModel, fwType, showInactive]);

  // Get axios instance from apiClient
  const axiosInstance = useMemo(() => apiClient.getAxiosInstance(), []);

  useEffect(() => {
    const fetchData = async () => {
      if (!isAuthenticated) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Fetch firmware versions from API. Soft-delete: backend defaults
        // to active-only; admins opt in via the "Show inactive" toggle.
        // Server-side paginated — page + pageSize from the firmware tab
        // state, search / model / type pushed through.
        const fwParams: Record<string, any> = {
          page: fwPage,
          limit: fwPageSize,
        };
        if (showInactive && canSeeInactive) fwParams.include_inactive = true;
        // /v2/firmware accepts model + type but not a generic search.
        // Search is applied client-side over the current page below.
        if (fwModel) fwParams.model = fwModel;
        if (fwType) fwParams.type = fwType;
        const firmwareResponse = await axiosInstance.get<FirmwareApiResponse>('/v2/firmware', {
          params: fwParams
        });

        // Store raw firmware items + pagination totals.
        setFirmwareItems(firmwareResponse.data.items);
        setFwTotal(firmwareResponse.data.total ?? firmwareResponse.data.items.length);
        setFwTotalPages(firmwareResponse.data.pages ?? 1);

        // Transform API response to component format (for stats/other uses)
        const firmwareData: FirmwareVersion[] = firmwareResponse.data.items.map(fw => ({
          id: `fw-${fw.id}`,
          version: fw.version,
          device_type: fw.type as 'voice' | 'local_alarm' | 'dtmf_server',
          release_date: fw.created_at,
          changelog: fw.changelog || 'No changelog available',
          file_size: fw.file_size || 0,
          checksum: fw.checksum || '',
          is_latest: true,
          active: fw.is_active
        }));

        setFirmwareVersions(firmwareData);

        // Fetch OTA stats
        try {
          const statsResponse = await axiosInstance.get<OtaStatsResponse>('/v2/firmware/stats');
          setOtaStats(statsResponse.data);
        } catch (statsError) {
          console.warn('[OTA UPDATES] Could not fetch stats:', statsError);
        }

        // Fetch tracked device versions
        try {
          const versionsResponse = await axiosInstance.get<DeviceVersionsResponse>('/v2/device-versions', {
            params: { limit: 500 }
          });

          // Group versions by device_id to create device status entries
          const deviceMap = new Map<string, DeviceVersionApiItem[]>();
          versionsResponse.data.items.forEach(item => {
            const existing = deviceMap.get(item.device_id) || [];
            existing.push(item);
            deviceMap.set(item.device_id, existing);
          });

          // Transform to DeviceUpdateStatus format
          const deviceStatusData: DeviceUpdateStatus[] = [];
          deviceMap.forEach((versions, deviceId) => {
            // Use the first version entry for basic device info
            const mainVersion = versions.find(v => v.type === 'MAIN') || versions[0];
            if (mainVersion) {
              // Find latest firmware for this model/type
              const latestFw = firmwareData.find(fw =>
                fw.device_type === mainVersion.type.toLowerCase() ||
                fw.device_type === 'voice' // fallback
              );

              deviceStatusData.push({
                device_id: deviceId,
                device_name: mainVersion.serial || deviceId,
                device_type: mainVersion.type.toLowerCase() as 'voice' | 'local_alarm' | 'dtmf_server',
                current_version: mainVersion.version,
                latest_version: latestFw?.version || mainVersion.version,
                needs_update: latestFw ? latestFw.version !== mainVersion.version : false,
                last_updated: mainVersion.last_check
              });
            }
          });

          setDeviceStatus(deviceStatusData);
        } catch (versionsError) {
          console.warn('[OTA UPDATES] Could not fetch device versions:', versionsError);
          setDeviceStatus([]);
        }

        setPendingUpdates([]);

      } catch (err: any) {
        console.error('[OTA UPDATES] Error fetching data:', err);

        if (err.response?.status === 404 || err.response?.status === 500) {
          setFirmwareVersions([]);
          setDeviceStatus([]);
          setPendingUpdates([]);
          setError('OTA tables not initialized. Please run database migration.');
        } else {
          setError(err.message || 'Failed to fetch OTA data');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [
    isAuthenticated, refreshTrigger, axiosInstance, showInactive, canSeeInactive,
    fwPage, fwPageSize, fwModel, fwType,
  ]);

  // Distinct models / types seen across the loaded firmware items.
  // Used to populate the filter dropdowns so users only see options
  // that will actually return rows.
  const fwModels = useMemo(
    () => Array.from(new Set(firmwareItems.map(fw => fw.model))).sort(),
    [firmwareItems]
  );
  const fwTypes = useMemo(
    () => Array.from(new Set(firmwareItems.map(fw => fw.type))).sort(),
    [firmwareItems]
  );

  // Server-paginated list. `firmwareItems` is the current page
  // filtered by model + type on the backend; fwActive and the
  // free-text search remain client-side over the visible page
  // because the backend has no equivalent params.
  const filteredFirmwareItems = useMemo(() => {
    const needle = fwSearch.trim().toLowerCase();
    return firmwareItems.filter(fw => {
      if (fwActive === 'active' && !fw.is_active) return false;
      if (fwActive === 'inactive' && fw.is_active) return false;
      if (needle) {
        const hay = [
          fw.version, fw.model, fw.changelog ?? '', fw.from_version ?? '',
        ].join(' ').toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [firmwareItems, fwSearch, fwActive]);

  const resetFwFilters = () => {
    setFwSearch('');
    setFwModel('');
    setFwType('');
    setFwActive('all');
  };

  const devicesNeedingUpdate = deviceStatus.filter(d => d.needs_update).length;
  const totalDevices = otaStats?.total_tracked_devices || deviceStatus.length;
  const updatePercentage = totalDevices > 0
    ? Math.round(((totalDevices - devicesNeedingUpdate) / totalDevices) * 100)
    : 100;
  const inProgressCount = pendingUpdates.filter(u => u.status === 'in_progress').length;

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'completed': return 'status-badge completed';
      case 'in_progress': return 'status-badge in-progress';
      case 'pending': return 'status-badge pending';
      case 'failed': return 'status-badge failed';
      default: return 'status-badge';
    }
  };

  if (loading) {
    return (
      <div className="ota-loading">
        <div className="loading-spinner"></div>
        <p>Loading OTA updates...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ota-error">
        <span className="error-icon">!</span>
        <h3>{t('otaUpdates.setupRequired')}</h3>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="ota-updates-container">
      {/* Stats Grid - Dashboard Style */}
      <div className="stats-grid">
        <div className="stat-card success">
          <div className="stat-icon">📊</div>
          <div className="stat-content">
            <div className="stat-value">{updatePercentage}%</div>
            <div className="stat-label">{t('otaUpdates.updateCoverage')}</div>
          </div>
          <div className="stat-meta">{totalDevices - devicesNeedingUpdate} of {totalDevices} up to date</div>
        </div>

        <div className="stat-card warning">
          <div className="stat-icon">⚠️</div>
          <div className="stat-content">
            <div className="stat-value">{devicesNeedingUpdate}</div>
            <div className="stat-label">{t('otaUpdates.needsUpdate')}</div>
          </div>
          <div className="stat-meta">devices require firmware</div>
        </div>

        <div className="stat-card primary">
          <div className="stat-icon">🔄</div>
          <div className="stat-content">
            <div className="stat-value">{inProgressCount}</div>
            <div className="stat-label">{t('otaUpdates.inProgress')}</div>
          </div>
          <div className="stat-meta">updates currently running</div>
        </div>

        <div className="stat-card secondary">
          <div className="stat-icon">📦</div>
          <div className="stat-content">
            <div className="stat-value">{otaStats?.total_firmware_versions || firmwareVersions.length}</div>
            <div className="stat-label">{t('otaUpdates.firmwareVersions')}</div>
          </div>
          <div className="stat-meta">available for deployment</div>
        </div>
      </div>

      {/* Main Content Section */}
      <div className="ota-section">
        {/* Tabs */}
        <div className="section-tabs">
          <button
            className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            Device Status
          </button>
          <button
            className={`tab-button ${activeTab === 'firmware' ? 'active' : ''}`}
            onClick={() => setActiveTab('firmware')}
          >
            Firmware Versions
          </button>
          <button
            className={`tab-button ${activeTab === 'pending' ? 'active' : ''}`}
            onClick={() => setActiveTab('pending')}
          >
            Pending Updates
            {pendingUpdates.length > 0 && (
              <span className="tab-badge">{pendingUpdates.length}</span>
            )}
          </button>
        </div>

        {/* Tab Content */}
        <div className="section-content">
          {activeTab === 'overview' && (
            <div className="devices-status-list">
              {deviceStatus.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">📱</div>
                  <h3>{t('otaUpdates.noDeviceVersionsTracked')}</h3>
                  <p>Devices will appear here after their first OTA check.</p>
                  {otaStats?.device_models && otaStats.device_models.length > 0 && (
                    <p className="empty-meta">Available models: {otaStats.device_models.join(', ')}</p>
                  )}
                </div>
              ) : (
                <table className="ota-table">
                  <thead>
                    <tr>
                      <th>{t('otaUpdates.device')}</th>
                      <th>{t('otaUpdates.type')}</th>
                      <th>{t('otaUpdates.currentVersion')}</th>
                      <th>{t('otaUpdates.latestVersion')}</th>
                      <th>{t('otaUpdates.status')}</th>
                      <th>{t('otaUpdates.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deviceStatus.map((device) => (
                      <tr key={device.device_id}>
                        <td>
                          <div className="device-info">
                            <strong>{device.device_name}</strong>
                            <span className="device-id">{device.device_id}</span>
                          </div>
                        </td>
                        <td>
                          <span className={`type-badge ${device.device_type}`}>
                            {device.device_type.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="version">{device.current_version}</td>
                        <td className="version">{device.latest_version}</td>
                        <td>
                          {device.needs_update ? (
                            <span className="status-badge needs-update">{t('otaUpdates.updateAvailable')}</span>
                          ) : (
                            <span className="status-badge up-to-date">{t('otaUpdates.upToDate')}</span>
                          )}
                        </td>
                        <td>
                          {device.needs_update && (
                            <button className="btn btn-sm btn-primary">
                              Update
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {activeTab === 'firmware' && (
            <div className="firmware-versions-list">
              {firmwareItems.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">📦</div>
                  <h3>{t('otaUpdates.noFirmwareVersions')}</h3>
                  <p>Add firmware using the "Add Firmware" button.</p>
                </div>
              ) : (
                <>
                  <div
                    className="firmware-filters"
                    role="search"
                    aria-label={t('otaUpdates.filtersLabel')}
                  >
                    <input
                      type="text"
                      data-testid="field-firmware-search"
                      className="firmware-filter-search"
                      placeholder={t('otaUpdates.filterSearchPlaceholder')}
                      value={fwSearch}
                      onChange={e => setFwSearch(e.target.value)}
                      aria-label={t('otaUpdates.filterSearchPlaceholder')}
                    />
                    <select
                      className="firmware-filter-select"
                      value={fwModel}
                      onChange={e => setFwModel(e.target.value)}
                      aria-label={t('otaUpdates.model')}
                    >
                      <option value="">{t('otaUpdates.filterAllModels')}</option>
                      {fwModels.map(m => (
                        <option key={m} value={m}>{m}</option>
                      ))}
                    </select>
                    <select
                      className="firmware-filter-select"
                      value={fwType}
                      onChange={e => setFwType(e.target.value)}
                      aria-label={t('otaUpdates.type')}
                    >
                      <option value="">{t('otaUpdates.filterAllTypes')}</option>
                      {fwTypes.map(typ => (
                        <option key={typ} value={typ}>{typ}</option>
                      ))}
                    </select>
                    <select
                      className="firmware-filter-select"
                      value={fwActive}
                      onChange={e => setFwActive(e.target.value as 'all' | 'active' | 'inactive')}
                      aria-label={t('otaUpdates.status')}
                    >
                      <option value="all">{t('otaUpdates.filterAllStatus')}</option>
                      <option value="active">{t('otaUpdates.filterActiveOnly')}</option>
                      <option value="inactive">{t('otaUpdates.filterInactiveOnly')}</option>
                    </select>
                    {canSeeInactive && (
                      <label className="filter-toggle-inactive" title={t('common.showInactiveHint')}>
                        <input
                          type="checkbox"
                          checked={showInactive}
                          onChange={(e) => setShowInactive(e.target.checked)}
                        />
                        {t('common.showInactive')}
                      </label>
                    )}
                    <button
                      type="button"
                      className="firmware-filter-reset"
                      onClick={resetFwFilters}
                      disabled={!fwSearch && !fwModel && !fwType && fwActive === 'all'}
                    >
                      {t('otaUpdates.filterReset')}
                    </button>
                    <span className="firmware-filter-count">
                      {t('otaUpdates.filterCount', {
                        shown: filteredFirmwareItems.length.toString(),
                        total: firmwareItems.length.toString(),
                      })}
                    </span>
                  </div>
                {filteredFirmwareItems.length === 0 ? (
                  <div className="empty-state">
                    <div className="empty-icon">🔎</div>
                    <p>{t('otaUpdates.filterNoMatches')}</p>
                  </div>
                ) : (
                <table className="ota-table firmware-table">
                  <thead>
                    <tr>
                      <th>{t('otaUpdates.version')}</th>
                      <th>{t('otaUpdates.model')}</th>
                      <th>{t('otaUpdates.type')}</th>
                      <th>{t('otaUpdates.fromVersion')}</th>
                      <th>{t('otaUpdates.fileUrl')}</th>
                      <th>{t('otaUpdates.size')}</th>
                      <th>{t('otaUpdates.status')}</th>
                      <th>{t('otaUpdates.created')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredFirmwareItems.map((fw) => (
                      <tr
                        key={fw.id}
                        className="firmware-row clickable"
                        onClick={() => {
                          setSelectedFirmware(fw);
                          setShowDetailModal(true);
                        }}
                      >
                        <td>
                          <span className="version-cell">{fw.version}</span>
                        </td>
                        <td>
                          <span className="model-cell">{fw.model}</span>
                        </td>
                        <td>
                          <span className={`type-badge ${fw.type.toLowerCase()}`}>
                            {fw.type}
                          </span>
                        </td>
                        <td>
                          <span className="from-version-cell">
                            {fw.from_version || 'Any'}
                          </span>
                        </td>
                        <td>
                          <div className="url-table-cell" onClick={(e) => e.stopPropagation()}>
                            <span className="url-truncated" title={fw.file_url}>
                              {fw.file_url ? '...' + fw.file_url.slice(-30) : '-'}
                            </span>
                            {fw.file_url && <CopyUrlButton url={fw.file_url} />}
                          </div>
                        </td>
                        <td>
                          <span className="size-cell">{formatFileSize(fw.file_size || 0)}</span>
                        </td>
                        <td>
                          <span className={`status-badge ${fw.is_active ? 'up-to-date' : 'pending'}`}>
                            {fw.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td>
                          <span className="date-cell">
                            {formatDate(fw.created_at)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                )}

                <Pagination
                  page={fwPage}
                  totalPages={fwTotalPages}
                  totalItems={fwTotal}
                  pageSize={fwPageSize}
                  itemLabel="firmware"
                  onPageChange={setFwPage}
                  onPageSizeChange={(s) => { setFwPageSize(s); setFwPage(1); }}
                />
                </>
              )}
            </div>
          )}

          {activeTab === 'pending' && (
            <div className="pending-updates-list">
              {pendingUpdates.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">✓</div>
                  <h3>{t('otaUpdates.noPendingUpdates')}</h3>
                  <p>All scheduled updates have been completed.</p>
                </div>
              ) : (
                <table className="ota-table">
                  <thead>
                    <tr>
                      <th>{t('otaUpdates.device')}</th>
                      <th>{t('otaUpdates.targetVersion')}</th>
                      <th>{t('otaUpdates.status')}</th>
                      <th>{t('otaUpdates.progress')}</th>
                      <th>{t('otaUpdates.scheduled')}</th>
                      <th>{t('otaUpdates.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingUpdates.map((update) => (
                      <tr key={update.id}>
                        <td>
                          <div className="device-info">
                            <strong>{update.device_name}</strong>
                            <span className="device-id">{update.device_id}</span>
                          </div>
                        </td>
                        <td className="version">v{update.firmware_version}</td>
                        <td>
                          <span className={getStatusBadgeClass(update.status)}>
                            {update.status.replace('_', ' ')}
                          </span>
                        </td>
                        <td>
                          {update.status === 'in_progress' && (
                            <div className="progress-bar">
                              <div
                                className="progress-fill"
                                style={{ width: `${update.progress}%` }}
                              />
                              <span className="progress-text">{update.progress}%</span>
                            </div>
                          )}
                          {update.status === 'pending' && <span className="muted">-</span>}
                        </td>
                        <td className="muted">
                          {update.scheduled_at && formatDateTime(update.scheduled_at)}
                        </td>
                        <td>
                          {update.status === 'pending' && (
                            <button className="btn btn-sm btn-secondary">
                              Cancel
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Activity Summary Footer */}
      <div className="ota-footer">
        <div className="activity-summary">
          <div className="activity-item">
            <span className="activity-icon">📦</span>
            <span className="activity-text">
              <strong>{otaStats?.total_firmware_versions || firmwareVersions.length}</strong> firmware versions available
            </span>
          </div>
          <div className="activity-item">
            <span className="activity-icon">📱</span>
            <span className="activity-text">
              <strong>{totalDevices}</strong> devices tracked
            </span>
          </div>
          <div className="activity-item">
            <span className="activity-icon">🔧</span>
            <span className="activity-text">
              <strong>{otaStats?.device_models?.length || 0}</strong> device models supported
            </span>
          </div>
        </div>
      </div>

      {/* Firmware Detail Modal */}
      <FirmwareDetailModal
        isOpen={showDetailModal}
        firmware={selectedFirmware}
        onClose={() => {
          setShowDetailModal(false);
          setSelectedFirmware(null);
        }}
        onSave={() => {
          // Refresh firmware list after save
          setLoading(true);
          const params: Record<string, any> = { limit: 100 };
          if (showInactive && canSeeInactive) params.include_inactive = true;
          axiosInstance.get<FirmwareApiResponse>('/v2/firmware', { params })
            .then(response => {
              setFirmwareItems(response.data.items);
              // Update selected firmware with new data
              if (selectedFirmware) {
                const updated = response.data.items.find(fw => fw.id === selectedFirmware.id);
                if (updated) setSelectedFirmware(updated);
              }
            })
            .finally(() => setLoading(false));
        }}
        onDelete={() => {
          // Refresh firmware list after delete
          setLoading(true);
          const params: Record<string, any> = { limit: 100 };
          if (showInactive && canSeeInactive) params.include_inactive = true;
          axiosInstance.get<FirmwareApiResponse>('/v2/firmware', { params })
            .then(response => setFirmwareItems(response.data.items))
            .finally(() => setLoading(false));
        }}
      />
    </div>
  );
};
